import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpGatewayService } from '../../global/services/http-gateway.service';
import {
  BaseUserContract,
  UserContract,
} from '../../global/store/authentication/types';
import {
  InviteAdminContract,
  InviteSalesOrgAdminContract,
} from '../store/sales-organizations/types';
import {
  UpdateUserContract,
  UpdateUserMapSettingsContract,
} from '../store/users/types';
import { HttpParams } from '@angular/common/http';
import { PlaceAutocompleteOption } from '../store/loops/types';
import { InviteClusterAdminContract } from '../store/clusters/types';

@Injectable({ providedIn: 'root' })
export class UsersApi {
  private httpService = inject(HttpGatewayService);

  public inviteSalesRep(
    salesRepIds: string[],
    salesOrganizationId: string,
    clusterId: string,
  ): Observable<UserContract> {
    return this.httpService.post<UserContract>(
      `/users/${salesOrganizationId}/${clusterId}/invite-sales-reps`,
      {
        salesRepIds,
      },
    );
  }

  public getUsers(): Observable<UserContract[]> {
    return this.httpService.get<UserContract[]>('/users');
  }

  public getUsersForSelection(
    orgId?: string,
    clusterId?: string,
  ): Observable<BaseUserContract[]> {
    let params = new HttpParams();
    if (orgId) params = params.set('orgId', orgId);
    if (clusterId) params = params.set('clusterId', clusterId);
    return this.httpService.get<BaseUserContract[]>(
      '/users/selectables',
      params,
    );
  }

  public inviteSalesOrgAdmin(
    inviteSalesOrgAdminContract: InviteSalesOrgAdminContract,
  ): Observable<InviteSalesOrgAdminContract> {
    return this.httpService.post<InviteSalesOrgAdminContract>(
      `/users/invite-sales-org-admin`,
      inviteSalesOrgAdminContract,
    );
  }

  public inviteClusterAdmin(
    inviteClusterAdminContract: InviteClusterAdminContract,
  ): Observable<InviteClusterAdminContract> {
    return this.httpService.post<InviteClusterAdminContract>(
      `/users/invite-cluster-admin`,
      inviteClusterAdminContract,
    );
  }

  public inviteAdmin(
    inviteAdminContract: InviteAdminContract,
  ): Observable<InviteAdminContract> {
    return this.httpService.post<InviteAdminContract>(
      `/users/invite-admin`,
      inviteAdminContract,
    );
  }

  public updateUser(
    user: UpdateUserContract,
    id: string,
  ): Observable<UserContract> {
    return this.httpService.put<UserContract>(`/users/${id}`, user);
  }

  public updateMapSettings(
    settings: UpdateUserMapSettingsContract,
    id: string,
  ): Observable<UserContract> {
    return this.httpService.put<UserContract>(
      `/users/${id}/map-settings`,
      settings,
    );
  }

  public getSalesRepsForCluster(
    salesOrganizationId: string,
    clusterId: string,
  ): Observable<UserContract[]> {
    return this.httpService.get<UserContract[]>(
      `/clusters/${salesOrganizationId}/${clusterId}/sales-reps`,
    );
  }

  public getHomeAddressAutocompleteOptions(
    input: string,
    sessionToken: string,
  ): Observable<PlaceAutocompleteOption[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('input', input);
    queryParams = queryParams.set('sessiontoken', sessionToken);
    return this.httpService.get<PlaceAutocompleteOption[]>(
      '/users/home-address/autocomplete',
      queryParams,
    );
  }

  public updateMapFilterDefaults(
    userId: string,
    filterKeys: string[],
  ): Observable<UserContract> {
    return this.httpService.put<UserContract>(
      `/users/${userId}/map-filter-defaults`,
      { filterKeys },
    );
  }

  public deactivateUser(id: string): Observable<UserContract> {
    return this.httpService.put<UserContract>(`/users/${id}/deactivate`, {});
  }

  public activateUser(id: string): Observable<UserContract> {
    return this.httpService.put<UserContract>(`/users/${id}/activate`, {});
  }

  public getUserById(id: string): Observable<UserContract> {
    return this.httpService.get<UserContract>(`/users/${id}`);
  }

  public resendInvite(userId: string): Observable<void> {
    return this.httpService.post<void>(`/users/${userId}/resend-invite`, {});
  }
}
